import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PaymentSetup from '@/components/PaymentSetup';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/stripe/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/stripe/create-portal-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating portal session:', err);
      toast.error('Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    await fetchSubscriptionStatus();
    if (refreshUser) refreshUser();
    toast.success('Your subscription is now active!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      trialing: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-red-100 text-red-800',
      cancelled: 'bg-stone-100 text-stone-800',
      cancelling: 'bg-amber-100 text-amber-800',
      none: 'bg-stone-100 text-stone-600'
    };
    
    const labels = {
      trialing: 'Free Trial',
      active: 'Active',
      past_due: 'Past Due',
      cancelled: 'Cancelled',
      cancelling: 'Cancelling',
      none: 'No Subscription'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.none}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-stone-900">Subscription</h1>
            <p className="text-stone-600">Manage your subscription and billing</p>
          </div>
        </div>

        {loading ? (
          <Card className="border-stone-100">
            <CardContent className="p-8 text-center">
              <p className="text-stone-500">Loading subscription details...</p>
            </CardContent>
          </Card>
        ) : subscription?.status === 'none' || !subscription?.has_payment_method ? (
          /* No subscription - show payment setup */
          <PaymentSetup onSuccess={handlePaymentSuccess} />
        ) : (
          /* Has subscription - show details */
          <div className="space-y-6">
            {/* Status Card */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center justify-between">
                  <span>Subscription Status</span>
                  {getStatusBadge(subscription.status)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscription.status === 'trialing' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-900">Free Trial Active</p>
                        <p className="text-sm text-blue-700">
                          Your trial ends on {formatDate(subscription.trial_end)}. 
                          You&apos;ll be charged $12.99/month after that.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {subscription.status === 'active' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-900">Subscription Active</p>
                        <p className="text-sm text-green-700">
                          Next billing date: {formatDate(subscription.current_period_end)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {subscription.status === 'past_due' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">Payment Failed</p>
                        <p className="text-sm text-red-700">
                          Please update your payment method to continue using BeautyPro Review.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {subscription.cancel_at_period_end && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-900">Cancellation Pending</p>
                        <p className="text-sm text-amber-700">
                          Your subscription will end on {formatDate(subscription.current_period_end)}.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Method Card */}
            {subscription.has_payment_method && (
              <Card className="border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-8 bg-stone-100 rounded flex items-center justify-center">
                        <span className="text-xs font-medium text-stone-600 uppercase">
                          {subscription.card_brand || 'Card'}
                        </span>
                      </div>
                      <span className="text-stone-700">
                        •••• •••• •••• {subscription.card_last4}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      className="h-9"
                    >
                      {portalLoading ? 'Loading...' : 'Update'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Manage Subscription Button */}
            <Button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {portalLoading ? 'Opening Portal...' : 'Manage Billing & Subscription'}
            </Button>

            {/* Security Note */}
            <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
              <Shield className="w-4 h-4" />
              <span>Payments secured by Stripe</span>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
